
import { api } from './base';
import type { UserInfo } from '@/entities/ticket';

export type ErrorReportKind = 'crash' | 'metric' | 'network';

export interface ErrorReportDto {
  message: string;
  kind?: ErrorReportKind;
  stack?: string;
  url?: string;
  userAgent?: string;
  comment?: string;
  contact?: string;
}

export type ErrorReportStatus = 'new' | 'triaged' | 'resolved';

export interface ErrorReport {
  id: string;
  userId: string | null;
  user?: UserInfo | null;
  kind: ErrorReportKind;
  message: string;
  stack: string | null;
  url: string | null;
  userAgent: string | null;
  comment: string | null;
  contact: string | null;
  status: ErrorReportStatus;
  fingerprint: string | null;
  occurrences: number;
  lastSeenAt: string | null;
  createdAt: string;
}

export interface ErrorLogCounts {
  byStatus: Record<string, number>;
  byKind: Record<string, number>;
}

export interface ErrorLogSettings {
  ticketGroupId: string | null;
  ticketCategoryId: string | null;
  retentionDaysTelemetry: number;
  retentionDaysCrash: number;
}

export const errorReportsApi = {
  send: (dto: ErrorReportDto) => api.post<{ id: string }>('/api/v1/error-reports', dto),

  adminList: (params?: {
    status?: ErrorReportStatus;
    kind?: ErrorReportKind;
    q?: string;
    limit?: number;
    offset?: number;
  }) =>
    api.get<ErrorReport[]>(
      '/api/v1/admin/error-reports',
      params as Record<string, string | number | undefined>,
    ),

  adminCounts: () => api.get<ErrorLogCounts>('/api/v1/admin/error-reports/counts'),

  adminSetStatus: (id: string, status: ErrorReportStatus) =>
    api.patch<ErrorReport>(`/api/v1/admin/error-reports/${id}`, { status }),

  adminBulk: (ids: string[], action: ErrorReportStatus | 'delete') =>
    api.post<{ affected: number }>('/api/v1/admin/error-reports/bulk', { ids, action }),

  adminCreateTicket: (id: string) =>
    api.post<{ ticketId: string }>(`/api/v1/admin/error-reports/${id}/ticket`, {}),

  adminGetSettings: () => api.get<ErrorLogSettings>('/api/v1/admin/error-log-settings'),

  adminUpdateSettings: (dto: Partial<ErrorLogSettings>) =>
    api.put<ErrorLogSettings>('/api/v1/admin/error-log-settings', dto),
};
